import { useQuery } from "@tanstack/react-query";
import { getTextBoundingBoxes as getTextBoundingBoxesApi } from "~/api/documents/documents";
import type { SearchMode } from "~/features/skyView/store/searchHighlight.slice";

export type BoundingBoxMatch = {
  box_2d: [number, number, number, number];
};

export type TextBoundingBoxesResponse = {
  matches: BoundingBoxMatch[];
  query: string;
  pageId: string;
};

type GetTextBoundingBoxesParams = {
  organizationId: string;
  projectId: string;
  pageId: string;
  query: string;
  mode: SearchMode;
};

const getTextBoundingBoxes = async ({
  organizationId,
  projectId,
  pageId,
  query,
  mode,
}: GetTextBoundingBoxesParams): Promise<TextBoundingBoxesResponse> => {
  const response = await getTextBoundingBoxesApi(
    organizationId,
    projectId,
    pageId,
    { query, mode }
  );
  return {
    query: response.query,
    pageId: response.pageId,
    matches: response.matches.map((match) => ({
      box_2d: match.box_2d as [number, number, number, number],
    })),
  };
};

export const useTextBoundingBoxes = (
  organizationId: string | undefined,
  projectId: string | undefined,
  pageId: string | undefined,
  query: string | null,
  mode: SearchMode = "exact"
) => {
  return useQuery({
    queryKey: [
      "textBoundingBoxes",
      organizationId,
      projectId,
      pageId,
      query,
      mode,
    ],
    queryFn: () =>
      getTextBoundingBoxes({
        organizationId: organizationId!,
        projectId: projectId!,
        pageId: pageId!,
        query: query!,
        mode,
      }),
    enabled:
      !!organizationId &&
      !!projectId &&
      !!pageId &&
      !!query &&
      query.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
